/**
 * caves.ts — deterministic CAVE SITES of the Commons (terrain wave 2026-09-08:
 * "caves that the user can explore"). Pure + seed-derived like settlements:
 * the same world seed yields identical sites on every client AND the server
 * (VERBATIM mirror in server/src/lib/caves.ts — the entrance apron moves the
 * ground, so the server's seating must know it). The client alone builds the
 * interiors (client/src/lib/cave_gen.ts); the server only needs the sites.
 *
 * A site is a hillside point in the mountain belts: high enough to be rock,
 * sloped enough for a portal face, clear of every settlement plateau, spaced
 * from other caves. The tunnel heads UPHILL into the hill from a level apron
 * cut at the mouth (the apron is the landform layer's `caveApronHeight`).
 */
import { settlementRng, settlementSites, settlementRegionCenters, SETTLEMENT_REGION_R } from './settlements.js';
import { terrainHeightBase, terraceHeight, PLATEAU_RADIUS, plateauSites } from './terrain.js';
import { SEA_LEVEL_M } from './constants.js';

export interface CaveSite {
  id: number;
  /** Mouth point (world m) and its apron level (m). */
  x: number; z: number; h: number;
  /** Unit heading INTO the hill (uphill at the mouth). */
  dx: number; dz: number;
  /** Per-site seed for the interior generator. */
  caveSeed: number;
}

/** Candidates per settled region and the cap of accepted caves per region. */
const CANDIDATES_PER_REGION = 600;
const CAVES_PER_REGION = 12;
/** Mouth height band: at least CAVE_ABOVE_SEA over the derived sea, at most CAVE_H1 (m). */
export const CAVE_ABOVE_SEA = 12;
export const CAVE_H1 = 700;
/** Mirror of sea_level.ts: the sea sits SETTLEMENT_MARGIN below the lowest settlement level. */
const SETTLEMENT_MARGIN_M = 8;
/** Hillside slope band at the mouth (rise/run) — gentle hills qualify; the worm digs down to stay under them. */
export const CAVE_SLOPE_MIN = 0.1;
export const CAVE_SLOPE_MAX = 1.0;
/** Clearance from any settlement plateau disc (m) and between caves (m). */
const CAVE_PLATEAU_CLEAR = 600;
const CAVE_SPACING = 1200;
/** Level apron at the mouth: disc radius and blend ring (m). */
export const CAVE_APRON_R = 7;
export const CAVE_APRON_RING = 9;

const memo = new Map<string, CaveSite[]>();

/**
 * The Commons sea level as caves.ts can compute it without the client's
 * sea_level.ts: SEA_LEVEL_M clamped below the lowest settlement PLATEAU
 * (settlement centres now sit exactly at their plateau level, so this equals
 * commonsSeaLevel(seed) — verify:caves asserts it).
 */
export function caveSeaLevel(seed: string): number {
  let lowest = Infinity;
  for (const p of plateauSites(seed)) if (p.h < lowest) lowest = p.h;
  return Math.min(SEA_LEVEL_M, lowest - SETTLEMENT_MARGIN_M);
}

/** The cave sites of a seed (memoised; shared array — never mutate). */
export function caveSites(seed: string): CaveSite[] {
  let s = memo.get(seed);
  if (!s) { s = generate(seed); memo.set(seed, s); }
  return s;
}

function generate(seed: string): CaveSite[] {
  const rng = settlementRng(seed + ':caves');
  const towns = settlementSites(seed);
  const out: CaveSite[] = [];
  const R = SETTLEMENT_REGION_R + 8000;
  const surf = (x: number, z: number): number => terraceHeight(seed, x, z, terrainHeightBase(seed, x, z));
  const sea = caveSeaLevel(seed);
  for (const [rcx, rcz] of settlementRegionCenters(seed)) {
    let accepted = 0;
    for (let k = 0; k < CANDIDATES_PER_REGION && accepted < CAVES_PER_REGION; k++) {
      const x = rcx + (rng() * 2 - 1) * R;
      const z = rcz + (rng() * 2 - 1) * R;
      const h = surf(x, z);
      if (h < sea + CAVE_ABOVE_SEA || h > CAVE_H1) continue;
      const gx = (surf(x + 4, z) - surf(x - 4, z)) / 8;
      const gz = (surf(x, z + 4) - surf(x, z - 4)) / 8;
      const g = Math.sqrt(gx * gx + gz * gz);
      if (g < CAVE_SLOPE_MIN || g > CAVE_SLOPE_MAX) continue;
      let clear = true;
      for (const t of towns) {
        const d = Math.sqrt((t.centerX - x) * (t.centerX - x) + (t.centerZ - z) * (t.centerZ - z));
        if (d < PLATEAU_RADIUS[t.tier] + CAVE_PLATEAU_CLEAR) { clear = false; break; }
      }
      if (!clear) continue;
      if (!out.every((c) => Math.sqrt((c.x - x) * (c.x - x) + (c.z - z) * (c.z - z)) >= CAVE_SPACING)) continue;
      const id = out.length;
      out.push({
        id, x, z, h, dx: gx / g, dz: gz / g,
        caveSeed: (Math.imul(Math.round(x * 10) | 0, 73856093) ^ Math.imul(Math.round(z * 10) | 0, 19349663) ^ Math.imul(id + 1, 0x9e3779b1)) >>> 0,
      });
      accepted++;
    }
  }
  return out;
}

/** Mouth footprint: along the heading from MOUTH_BACK behind the mouth to MOUTH_IN into the hill. */
export const CAVE_MOUTH_BACK = 1.5;
export const CAVE_MOUTH_IN = 9;
/** Half-width (m) of the mouth cut through the terrain surface. */
export const CAVE_MOUTH_HALF_W = 3.2;

/**
 * True when (x, z) lies in a cave's MOUTH footprint — the strip of terrain
 * surface the tunnel passes through. The chunk assembler drops every terrain
 * triangle whose centroid is in it (the hole the tunnel enters by); the far
 * tier ignores it (sub-cell at 64 m). Pure; both mirrors carry it, only the
 * client's mesh builder calls it.
 */
export function caveMouthMask(seed: string, x: number, z: number): boolean {
  for (const c of caveSites(seed)) {
    const px = x - c.x, pz = z - c.z;
    if (px * px + pz * pz > (CAVE_MOUTH_IN + 2) * (CAVE_MOUTH_IN + 2)) continue;
    const s = px * c.dx + pz * c.dz;
    if (s < -CAVE_MOUTH_BACK || s > CAVE_MOUTH_IN) continue;
    const t = -px * c.dz + pz * c.dx;
    if (Math.abs(t) <= CAVE_MOUTH_HALF_W) return true;
  }
  return false;
}

/** The cave sites whose mouth footprint can touch a square [x0,x1]×[z0,z1]. */
export function caveSitesNear(seed: string, x0: number, z0: number, x1: number, z1: number, margin = CAVE_MOUTH_IN + 4): CaveSite[] {
  return caveSites(seed).filter((c) => c.x >= x0 - margin && c.x <= x1 + margin && c.z >= z0 - margin && c.z <= z1 + margin);
}
